import React from "react";
import { useNavigate } from "react-router-dom";
import useMobile from "../../hooks/useMobile";
import { ICooperativeDetails } from "../../exports/interfaces";
import CooperativeInfo from "./CooperativeInfo";
import AddressInfo from "./AddressInfo";
import FinancialInfo from "./FinancialInfo";
import CommitteesInfo from "./CommitteesInfo";
import MemberInfo from "./MemberInfo";
import DocumentsInfo from "./DocumentsInfo";
import { UserRoundPlus } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { darkenColor } from "../../exports/functions";

const natureClass = (nature: string) => {
  switch (nature) {
    case "Multipurpose":
      return "pill-info";
    case "Saving and Credit":
      return "pill-danger";
    case "Agriculture":
      return "pill-success";
    case "Others":
      return "pill-primary";
    default:
      return "bg-secondary";
  }
};

const CooperativeDetails = ({
  cooperative,
}: {
  cooperative: ICooperativeDetails | null;
}) => {
  const { statusOptions } = useOption();
  const getStepClass = (status: string) => {
    switch (status) {
      case "Complete":
        return "pill-success";
      case "In-Complete":
        return "pill-warning";
      case "Not Started":
        return "pill-danger";
      default:
        return "bg-secondary-subtle text-secondary";
    }
  };
  const ignoredKeys = [
    "id",
    "createdAt",
    "updatedAt",
    "file",
    "fileName",
    "documentUrl",
    "bodFile",
    "bodFileName",
    "bodDocumentUrl",
  ];

  const isFilled = (obj: any) => {
    if (!obj) return false;

    return Object.entries(obj).every(([key, value]) => {
      if (
        ignoredKeys.includes(key) ||
        key.endsWith("File") ||
        key.endsWith("FileName") ||
        key.endsWith("DocumentUrl")
      ) {
        return true;
      }

      // Boolean values are valid, including false
      if (typeof value === "boolean") {
        return true;
      }

      // Arrays must contain at least one item
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== null && value !== "" && value !== undefined;
    });
  };

  const isTableFilled = (table: any[]) => {
    if (!table || table.length === 0) return false;

    return table.every((item) =>
      Object.entries(item).every(([key, value]) => {
        if (ignoredKeys.includes(key)) {
          return true;
        }

        if (key === "husbandName" && item.maritalStatus === "Single") {
          return true;
        }

        if (
          key.endsWith("File") ||
          key.endsWith("FileName") ||
          key.endsWith("DocumentUrl")
        ) {
          return true;
        }

        if (typeof value === "boolean") {
          return true;
        }

        return value !== null && value !== "" && value !== undefined;
      }),
    );
  };

  const getCooperativeCompletionStatus = () => {
    if (!cooperative) return "In-Complete";

    const basicInfo = isFilled({
      businessregistrationNumber: cooperative.businessregistrationNumber,
      contactPerson: cooperative.contactPerson,
      email: cooperative.email,
      maleMembers: cooperative.maleMembers,
      femaleMembers: cooperative.femaleMembers,
      mobileNumber: cooperative.mobileNumber,
      nameEnglish: cooperative.nameEnglish,
      nameNepali: cooperative.nameNepali,
      natureOfWork: cooperative.natureOfWork,
      otherMembers: cooperative.otherMembers,
      panNumber: cooperative.panNumber,
      registeredAt: cooperative.registeredAt,
      registeredDate: cooperative.registeredDate,
      registeredEnglishDate: cooperative.registeredEnglishDate,
      registeredOfficeAddress: cooperative.registeredOfficeAddress,
      registrationNumber: cooperative.registrationNumber,
      softwareUsed: cooperative.softwareUsed,
      staffCount: cooperative.staffCount,
      womenLead: cooperative.womenLead,
      workingArea: cooperative.workingArea,
    });

    const addressFilled = isFilled(cooperative.address);

    const financialFilled =
      isFilled(cooperative.financial?.share) &&
      isFilled(cooperative.financial?.loan) &&
      isFilled(cooperative.financial?.income) &&
      isFilled(cooperative.financial?.audit);

    const committeeFilled =
      isTableFilled(
        cooperative.cooperativeandexecutive?.committeemember || [],
      ) &&
      isTableFilled(cooperative.cooperativeandexecutive?.executivemember || []);

    const shareMemberFilled = isFilled(cooperative.shareMember);

    const documentsFilled = isFilled(cooperative.documents);

    const declarationFilled = isFilled(cooperative.declaration);

    const isComplete =
      basicInfo &&
      addressFilled &&
      financialFilled &&
      committeeFilled &&
      shareMemberFilled &&
      documentsFilled &&
      declarationFilled;

    return isComplete ? "Complete" : "In-Complete";
  };

  const completionStatus = getCooperativeCompletionStatus();

  const isMobile = useMobile();
  const navigate = useNavigate();

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Cooperative Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-3">Cooperatives Details</h3>
        </div>

        <button
          className="btn px-3 btn-badge"
          onClick={() => navigate("/register-new")}
        >
          <i className={`${!isMobile ? "me-2" : ""}`}>
            <UserRoundPlus size={20} />
          </i>
          {!isMobile && "Register Here"}
        </button>
      </div>

      <div className="dashboard-card p-3">
        {/* Top Section */}
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: "#ddd",
              }}
            />

            {!isMobile && (
              <div>
                <h4 className="mb-1">
                  {cooperative?.nameEnglish}
                  <span
                    className={`badge ${natureClass(cooperative?.natureOfWork || "-")} ms-2`}
                  >
                    {cooperative?.natureOfWork}
                  </span>
                  <span
                    className="badge ms-2"
                    style={statusStyle(cooperative?.status || "")}
                  >
                    {cooperative?.status}
                  </span>
                  <span
                    className={`badge ms-2 ${getStepClass(completionStatus)}`}
                  >
                    {completionStatus}
                  </span>
                </h4>

                <small>{cooperative?.mobileNumber}</small>
                <br />
                <small className="text-primary">{cooperative?.email}</small>
                <br />
                <small>
                  {cooperative?.address?.municipality},{" "}
                  {cooperative?.address?.district},{" "}
                  {cooperative?.address?.province}
                </small>
              </div>
            )}
          </div>

          {/* {!isMobile && (
            <button className="btn px-3 btn-badge fs-small">
              <i className={`fa-solid fa-pen ${!isMobile ? "me-2" : ""}`}></i>
              {!isMobile && "Edit"}
            </button>
          )} */}
          {isMobile && (
            <div className="text-end">
              {/* <button className="btn px-3 btn-badge fs-small">
                <i className={`fa-solid fa-pen ${!isMobile ? "me-2" : ""}`}></i>
                {!isMobile && "Edit"}
              </button> */}
              <h4 className="mb-1">
                {cooperative?.nameEnglish}
                <span
                  className={`badge ${natureClass(cooperative?.natureOfWork || "-")} ms-2 mb-2`}
                >
                  {cooperative?.natureOfWork}
                </span>
                <span
                  className="badge ms-2"
                  style={statusStyle(cooperative?.status || "")}
                >
                  {cooperative?.status}
                </span>
                <span
                  className={`badge ms-2 ${getStepClass(completionStatus)}`}
                >
                  {completionStatus}
                </span>
              </h4>

              <small>{cooperative?.mobileNumber}</small>
              <br />
              <small className="text-primary">{cooperative?.email}</small>
              <br />
              <small>
                {cooperative?.address?.municipality},{" "}
                {cooperative?.address?.district},{" "}
                {cooperative?.address?.province}
              </small>
            </div>
          )}
        </div>

        <hr />

        <CooperativeInfo cooperative={cooperative} />

        <AddressInfo cooperative={cooperative} />

        <FinancialInfo cooperative={cooperative} />

        <CommitteesInfo cooperative={cooperative} />

        <MemberInfo cooperative={cooperative} />

        <DocumentsInfo cooperative={cooperative} />
      </div>
    </>
  );
};

export const Info = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="col-md-4 details-text">
    <small className="d-block">{label}</small>
    <p>{value}</p>
  </div>
);

export default CooperativeDetails;
